<template>
  <div>
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
    />

    <v-card v-else>
      <v-card-title class="d-flex justify-space-between align-center">
        未印刷・印刷失敗の伝票
        <v-btn
          small
          outlined
          color="primary"
          :ripple="false"
          :loading="isReloading"
          @click="reload"
        >
          <v-icon
            small
            left
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          再読み込み
        </v-btn>
      </v-card-title>

      <v-card-subtitle>
        印刷に失敗した伝票や、送信後に完了通知が届いていない伝票の一覧です。再送するとプリンターが次回受信時に再印刷します。
        <span
          v-if="printTickets.length >= INDEX_LIMIT"
          class="error--text"
        >
          （表示は最新{{ INDEX_LIMIT }}件まで。古い送信済み伝票は表示されません）
        </span>
      </v-card-subtitle>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="printTickets"
          :header-props="{ sortByText: 'ソート' }"
          :items-per-page="20"
          :footer-props="{ itemsPerPageText: '表示件数', itemsPerPageOptions: [20, 50, 100] }"
          no-data-text="再送が必要な伝票はありません"
        >
          <template #[`item.status`]="{item}">
            <v-chip
              x-small
              :color="statusColor(item.status)"
              outlined
            >
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>

          <template #[`item.createdAt`]="{item}">
            {{ formatDateTime(item.createdAt) }}
          </template>

          <template #[`item.errorCode`]="{item}">
            <span v-if="item.errorCode">{{ item.errorCode }}</span>
            <span
              v-else
              class="text--disabled"
            >—</span>
          </template>

          <template #[`item.actions`]="{item}">
            <yona-edit-dialog
              v-if="item.status === 'sent'"
              title="再送の確認"
              btn-color="warning"
              save-text="二重印刷を承知の上で再送"
              :disabled="!item.resendable"
              @save="resend(item)"
            >
              <template #append-outer-display-name>
                <v-btn
                  x-small
                  outlined
                  color="warning"
                  :disabled="!item.resendable"
                  :loading="resendingId === item.id"
                >
                  再送
                </v-btn>
              </template>

              <template #input>
                <span>
                  この伝票は送信済みです。すでに印刷されている可能性があるため、再送すると<strong>二重印刷</strong>になる恐れがあります。再送してよろしいですか？
                </span>
              </template>
            </yona-edit-dialog>

            <v-btn
              v-else
              x-small
              outlined
              color="primary"
              :disabled="!item.resendable"
              :loading="resendingId === item.id"
              @click="resend(item)"
            >
              再送
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  ref,
  getCurrentInstance,
} from '@vue/composition-api'
import { mdiRefresh } from '@mdi/js'
import { findIndex } from 'lodash'
import PrintTicketApi from '@/api/waiter/PrintTicket'
import useCompInit from '@/views/composable/useCompInit'
import YonaEditDialog from '@/views/components/util/YonaEditDialog.vue'

export default {
  components: {
    YonaEditDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { isLoading, initWith } = useCompInit()
    // バックエンドの一覧上限(api/v1/waiter/print_tickets_controller INDEX_LIMIT)と揃える。
    // この件数に達したら「最新N件まで」の注記を表示する。
    const INDEX_LIMIT = 200
    const printTickets = ref([])
    const isReloading = ref(false)
    const resendingId = ref(null)

    const statusLabel = status => ({
      sent: '送信済み（未完了）',
      failed: '印刷失敗',
    }[status] || status)

    const statusColor = status => ({
      sent: 'warning',
      failed: 'error',
    }[status] || 'default')

    const formatDateTime = value => {
      if (!value) return '—'

      return new Date(value).toLocaleString('ja-JP', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    const getPrintTickets = async () => {
      const res = await PrintTicketApi.getPrintTickets()
      if (res?.status === 200) {
        printTickets.value = [...res.data.printTickets]
      }
    }

    const reload = async () => {
      isReloading.value = true
      await getPrintTickets()
      isReloading.value = false
    }

    const resend = async item => {
      resendingId.value = item.id
      try {
        const res = await PrintTicketApi.resendPrintTicket(item.id)
        if (res?.data) {
          // pending に戻ったチケットは再送候補一覧から外れるため除去する
          const idx = findIndex(printTickets.value, o => +o.id === +item.id)
          if (idx !== -1) printTickets.value.splice(idx, 1)
          vm.$toast.success('再送しました。プリンターが次回受信時に印刷します')
        }
      } catch {
        vm.$toast.error('再送に失敗しました')
      } finally {
        resendingId.value = null
      }
    }

    initWith([
      getPrintTickets(),
    ])

    return {
      isLoading,
      isReloading,
      resendingId,
      INDEX_LIMIT,
      printTickets,
      statusLabel,
      statusColor,
      formatDateTime,
      reload,
      resend,
      headers: [
        {
          text: 'ステータス', value: 'status', align: 'center',
        },
        {
          text: '商品', value: 'itemName',
        },
        {
          text: 'プリンター', value: 'kitchenPrinterName',
        },
        {
          text: 'エラー', value: 'errorCode', align: 'center',
        },
        {
          text: '作成日時', value: 'createdAt', align: 'center',
        },
        {
          text: '', value: 'actions', align: 'right', sortable: false,
        },
      ],
      icons: {
        mdiRefresh,
      },
    }
  },
}
</script>
